import React from 'react';
import { ModelUsageDetail } from '../../../utils/openai-api';
import { CurrencyAmount } from '../../CurrencyAmount';
import { formatNumber } from '../utils/formatters';
import { getModelUsageDisplay } from '../utils/modelUsageDisplay';

interface ModelUsageSectionProps {
  showModelUsage: boolean;
  setShowModelUsage: (show: boolean) => void;
  includesAnthropic: boolean;
  sortedModelUsage: ModelUsageDetail[];
  modelSortKey: string;
  modelSortDir: 'asc' | 'desc';
  handleModelSort: (key: string) => void;
}

export const ModelUsageSection: React.FC<ModelUsageSectionProps> = React.memo(({
  showModelUsage,
  setShowModelUsage,
  includesAnthropic,
  sortedModelUsage,
  modelSortKey,
  modelSortDir,
  handleModelSort,
}) => {
  return (
    <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 shadow-sm">
      <div className="px-2 sm:px-4 py-2 border-b border-gray-200 dark:border-gray-700 bg-gradient-to-r from-gray-50 to-gray-100 dark:from-gray-800 dark:to-gray-700 rounded-t-lg">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-1 sm:gap-2 min-w-0 flex-1">
            <svg className="w-4 h-4 flex-shrink-0 text-gray-600 dark:text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.75 17L9 20l-1 1h8l-1-1-.75-3M3 13h18M5 17h14a2 2 0 002-2V5a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
            </svg>
            <h2 className="text-xs sm:text-sm font-semibold text-gray-900 dark:text-white truncate">Model Usage Statistics</h2>
          </div>
          <button
            onClick={() => setShowModelUsage(!showModelUsage)}
            className="p-1 rounded-md hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors"
            title={showModelUsage ? "Collapse section" : "Expand section"}
          >
            <svg
              className={`w-5 h-5 text-gray-500 dark:text-gray-400 transition-transform ${showModelUsage ? '' : 'rotate-180'}`}
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
            </svg>
          </button>
        </div>
      </div>
      {showModelUsage && (
      <>
      <div className="overflow-x-auto custom-scrollbar">
        <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
          <thead className="bg-gray-50 dark:bg-gray-900">
            <tr>
              {([
                ['model', 'Model Name', 'text-left', ''],
                ['contextTokens', 'Input Tokens', 'text-right', ''],
                ['generatedTokens', 'Output Tokens', 'text-right', ''],
                ['cachedTokens', 'Cached Tokens', 'text-right', 'hidden md:table-cell'],
                ['cacheRate', 'Cache Rate', 'text-right', 'hidden sm:table-cell'],
                ['cost', 'Total Cost', 'text-right', ''],
                ['numberOfRequests', 'Requests / Searches', 'text-right', ''],
                ['lastUsed', 'Last Used', 'text-right', 'hidden md:table-cell'],
              ] as const).map(([key, label, align, hideClass]) => (
                <th
                  key={key}
                  className={`px-2 sm:px-3 py-1 sm:py-1.5 ${align} text-[10px] sm:text-[11px] font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-800 select-none ${hideClass}`}
                  onClick={() => handleModelSort(key)}
                >
                  {label}
                  {modelSortKey === key && (
                    <span className="ml-1">{modelSortDir === 'asc' ? '▲' : '▼'}</span>
                  )}
                </th>
              ))}
            </tr>
          </thead>
          <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
            {sortedModelUsage.map((model, idx) => {
              const display = getModelUsageDisplay(model);
              return (
              <tr
                key={`${model.provider ?? 'unknown'}:${model.model}`}
                className={idx % 2 === 0 ? 'bg-white dark:bg-gray-800' : 'bg-gray-50 dark:bg-gray-900'}
              >
                <td className="px-2 sm:px-3 py-1 sm:py-1.5 whitespace-nowrap text-xs font-medium text-gray-900 dark:text-white">
                  <div className="flex flex-col">
                    <span>{model.model}</span>
                    {display.description && (
                      <span className="text-[11px] font-normal text-slate-500 dark:text-slate-400">
                        {display.description}
                      </span>
                    )}
                    {model.provider && (
                      <span className="text-[10px] font-semibold uppercase tracking-wide text-slate-500 dark:text-slate-400">
                        {model.provider === 'anthropic' ? 'Anthropic' : 'OpenAI'}
                      </span>
                    )}
                  </div>
                </td>
                <td className="px-2 sm:px-3 py-1 sm:py-1.5 whitespace-nowrap text-xs text-right text-gray-500 dark:text-gray-400">
                  {display.isSearchCharge || display.isSearchActivity ? '—' : formatNumber(model.contextTokens)}
                </td>
                <td className="px-2 sm:px-3 py-1 sm:py-1.5 whitespace-nowrap text-xs text-right text-gray-500 dark:text-gray-400">
                  {display.isSearchCharge || display.isSearchActivity ? '—' : formatNumber(model.generatedTokens)}
                </td>
                <td className="hidden md:table-cell px-2 sm:px-3 py-1 sm:py-1.5 whitespace-nowrap text-xs text-right text-gray-500 dark:text-gray-400">
                  {display.isSearchCharge || display.isSearchActivity ? '—' : formatNumber(model.cachedTokens)}
                </td>
                <td className="hidden sm:table-cell px-2 sm:px-3 py-1 sm:py-1.5 whitespace-nowrap text-xs text-right text-gray-500 dark:text-gray-400">
                  {model.contextTokens > 0
                    ? <span className={model.cachedTokens / model.contextTokens >= 0.5 ? 'text-emerald-500 dark:text-emerald-400 font-medium' : ''}>
                        {(model.cachedTokens / model.contextTokens * 100).toFixed(1)}%
                      </span>
                    : <span className="text-gray-400 dark:text-gray-600">—</span>
                  }
                </td>
                <td className="px-2 sm:px-3 py-1 sm:py-1.5 whitespace-nowrap text-xs text-right text-gray-500 dark:text-gray-400">
                  {display.isSearchActivity ? (
                    <span title="Search fees are listed under OpenAI Web Search">別行に計上</span>
                  ) : <CurrencyAmount
                    amount={model.cost}
                    className="text-xs font-semibold text-green-600 dark:text-green-400"
                  />}
                </td>
                <td className="px-2 sm:px-3 py-1 sm:py-1.5 whitespace-nowrap text-xs text-right text-gray-500 dark:text-gray-400">
                  <span title={display.isSearchCharge ? '請求データの検索回数。Total Requests には含めません。' : undefined}>
                    {display.quantity === null ? '不明' : formatNumber(display.quantity)}
                    {display.unit && <span className="ml-1 text-[11px]">{display.unit}</span>}
                  </span>
                </td>
                <td className="hidden md:table-cell px-2 sm:px-3 py-1 sm:py-1.5 whitespace-nowrap text-xs text-right text-gray-500 dark:text-gray-400">
                  {model.lastUsed ?? '—'}
                </td>
              </tr>
              );
            })}
          </tbody>
        </table>
      </div>
      {sortedModelUsage.some(model => {
        const display = getModelUsageDisplay(model);
        return display.isSearchCharge || display.isSearchActivity;
      }) && (
        <div className="px-2 sm:px-4 py-2 text-xs text-slate-600 dark:text-slate-300">
          <p>Web Search は検索ツール料金です。searches は請求データの検索回数、runs は実行記録の件数で、同じ単位ではありません。検索回数は Total Requests に加算しません。</p>
          {sortedModelUsage.some(model => {
            const display = getModelUsageDisplay(model);
            return display.isSearchCharge && display.quantity === null;
          }) && <p className="mt-1">検索回数は、請求データに数量がない場合や、一部のキーに絞り込んだ場合は不明です。キー別の検索料金は配賦した推定額です。</p>}
        </div>
      )}
      {includesAnthropic && (
        <div className="px-2 sm:px-4 py-1 text-[11px] text-slate-500 dark:text-slate-400">
          ※Anthropic の「Requests」はトークン数からの推定値です（Claude Code: 25,000 tok/req、API: 5,000 tok/req）。
        </div>
      )}
      </>
      )}
    </div>
  );
});
ModelUsageSection.displayName = 'ModelUsageSection';
